import { PageTopBar } from 'app/(authenticated)/_components/PageTopBar'
import { useParams, usePathname } from 'next/navigation'
import { FC, useMemo } from 'react'
import {
  LEVEL_WORKSPACE_DD_MENU_OPTIONS_HIGH_PERMISSIONS,
  LEVEL_WORKSPACE_DD_MENU_OPTIONS_LOW_PERMISSIONS,
  NAVIGATION_DD_MENU_CLICK,
  WORKSPACES_DD_MENU_PART,
} from 'src/utils/cmsNavigationUtils'

import { WorkspaceGroup } from '@models/types'

import { CommandPaletteProvider } from '../complexUI/CommandPalette'
import { styled } from '../stitches.config'

export type AuthenticatedCenteredTemplateProps = {
  children?: React.ReactNode
  logout: () => Promise<void>
  workspacePermissions: WorkspaceGroup[]
  textContainerSize?: 'medium' | 'large'
}

export const AuthenticatedCenteredTemplate: FC<AuthenticatedCenteredTemplateProps> = ({
  children,
  logout,
  workspacePermissions,
  textContainerSize = 'medium',
}) => {
  const params = useParams()
  const brand = params.brand as string
  const siteId = params.siteId as string
  const workspace = params.workspace as string
  const pathname = usePathname()
  const dropdownMenuOptions = useMemo(() => {
    const options =
      workspacePermissions.includes('Admin') || workspacePermissions.includes('Accounting')
        ? LEVEL_WORKSPACE_DD_MENU_OPTIONS_HIGH_PERMISSIONS
        : LEVEL_WORKSPACE_DD_MENU_OPTIONS_LOW_PERMISSIONS

    if (pathname !== '/') {
      return [...(WORKSPACES_DD_MENU_PART || []), ...(options || [])]
    }

    return options
  }, [workspacePermissions, pathname])

  return (
    <CommandPaletteProvider workspacePermissions={workspacePermissions}>
      <Container>
        <PageTopBar
          viewState="default"
          showCommandPaletteTrigger
          dropdownMenuOptions={dropdownMenuOptions}
          dropdownMenuItemClick={NAVIGATION_DD_MENU_CLICK({
            workspace,
            brand,
            siteId,
            logout,
          })}
          css={{
            zIndex: 2000,
            boxShadow: '$bottom',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
          }}
        />
        <TextContainer textContainerSize={textContainerSize}>{children}</TextContainer>
      </Container>
    </CommandPaletteProvider>
  )
}

const Container = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100dvh',
})
const TextContainer = styled('div', {
  textAlign: 'center',
  color: '$gs12',
  paddingTop: '80px',
  pb: '$10',
  variants: {
    textContainerSize: {
      medium: {
        maxWidth: '650px',
      },
      large: {
        maxWidth: '750px',
      },
    },
  },
})
